import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  message, 
  Button,
  Space,
  Row,
  Col,
  Statistic,
  Empty
} from 'antd';
import { 
  UserOutlined, 
  ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface Student {
  id: string;
  name: string;
  email: string;
  student_id: string;
  class_name: string;
  created_at: string;
  test_count: number;
  answer_count: number;
  avg_score: number;
}

const StudentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/teacher/login');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchStudents();
  }, [navigate]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/students');
      setStudents(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('teacher');
        navigate('/teacher/login');
        message.error('Session expired. Please log in again.');
      } else {
        message.error('Failed to fetch students');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Student Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Student ID',
      dataIndex: 'student_id',
      key: 'student_id',
      width: 120,
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (text: string) => text || '-',
    },
    {
      title: 'Tests Taken',
      dataIndex: 'test_count',
      key: 'test_count',
      width: 100,
      render: (count: number) => (
        <Tag color="blue">{count || 0}</Tag>
      ),
    },
    {
      title: 'Avg Score',
      dataIndex: 'avg_score',
      key: 'avg_score',
      width: 100,
      render: (score: number) => (
        score ? `${score.toFixed(1)}%` : '-'
      ),
    },
    {
      title: 'Registered',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  return (
    <div>
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/teacher')}
          >
            Back to Dashboard
          </Button>
          <h1>Student Management</h1>
        </div>
      </div>

      <div className="container">
        <Row gutter={16} style={{ marginBottom: '20px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Students"
                value={students.length}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Tests per Student"
                value={
                  students.length > 0
                    ? (students.reduce((sum, s) => sum + s.test_count, 0) / students.length).toFixed(1)
                    : 0
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Performance"
                value={
                  students.length > 0
                    ? (students.reduce((sum, s) => sum + (s.avg_score || 0), 0) / students.length).toFixed(1)
                    : 0
                }
                suffix="%"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Users"
                value={students.filter(s => s.test_count > 0).length}
              />
            </Card>
          </Col>
        </Row>

        <Card title="📋 All Students">
          {students.length === 0 ? (
            <Empty 
              description="No students registered yet"
              style={{ marginTop: '50px' }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={students}
              rowKey="id"
              loading={loading}
              pagination={{ 
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 1200 }}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default StudentManagement;
